/**
 * Czy magazyn z oferty może dostać dotację PME2 i ile orientacyjnie.
 *
 * Czyste funkcje, bez Reacta. Liczby programu stoją w `config/pme2.js` (ze źródłem
 * i datą), tutaj jest tylko logika.
 *
 * ═══ ZASADY ═══
 * - Nie zgadujemy. „nie” tylko wtedy, gdy liczby z oferty przesądzają sprawę
 *   (pojemność, zasada 2:1, cena samego magazynu ponad limit). Czego z oferty
 *   nie widać (EMS, cena na fakturze), to „sprawdz”, a warunki osobiste to „ty”.
 * - Cena oferty to całość, więc nie liczymy z niej 30%. Podstawą szacunku jest cena
 *   samego magazynu w sklepach (dolna granica, bo na fakturze dochodzi montaż),
 *   a górną granicą kwota z pojemności i limitu. Nagłówek „do 16 000 zł” przy
 *   magazynie za 12 000 zł obiecywałby czterokrotnie za dużo.
 * - Każdy warunek ma krótki `tytul`, jedną krótką wartość `krotko` do listy w raporcie
 *   i pełny `opis` pod rozwinięciem (przebudowa 2026-09-30: mniej tekstu na widoku).
 */
import {
  MIN_POJEMNOSC_KWH, KWH_NA_KWP, MAKS_CENA_ZA_KWH, PROCENT_KOSZTOW, ZL_ZA_KWH,
  LIMIT_NET_BILLING, LIMIT_NET_METERING, PREMIA_UE, LIMIT_NA_PPE,
} from '../config/pme2'

// Dwa miejsca po przecinku: 5,12 kWh i 9,87 kWp to wartości z karty, nie do zaokrąglania.
const liczba = (x) => String(Math.round(x * 100) / 100).replace('.', ',')
const zl = (x) => `${(Math.round(x / 10) * 10).toLocaleString('pl-PL')} zł`

/**
 * Pojemność magazynu z oferty: wybrany model, wpisana pojemność albo wspólna
 * pojemność wszystkich pasujących modeli. Inaczej null (nie wiemy, więc nie oceniamy).
 */
export const pojemnoscMagazynu = (wybor, kandydaci = []) => {
  const zWyboru = Number(wybor?.model?.moc ?? wybor?.moc)
  if (Number.isFinite(zWyboru) && zWyboru > 0) return zWyboru
  const rozne = new Set(kandydaci.map((k) => k.pojemnosc_kwh).filter((p) => p > 0))
  return rozne.size === 1 ? [...rozne][0] : null
}

const wyspa = (zasilanieAwaryjne, backupFalownika) => {
  if (zasilanieAwaryjne === 'tak') {
    return { status: 'tak', krotko: 'jest w ofercie', opis: 'Oferta obejmuje zasilanie awaryjne.' }
  }
  if (zasilanieAwaryjne === 'nie') {
    if (backupFalownika === 'nie') {
      return { status: 'nie', krotko: 'brak w ofercie', opis: 'Oferta nie obejmuje zasilania awaryjnego, a ten falownik potrzebuje do niego osobnego modułu. Bez pracy wyspowej magazyn nie dostanie dotacji.' }
    }
    return { status: 'sprawdz', krotko: 'brak w ofercie', opis: backupFalownika === 'tak'
      ? 'Falownik ma zasilanie awaryjne, ale oferta go nie obejmuje. Do dotacji instalacja musi móc pracować przy braku prądu.'
      : 'Oferta nie obejmuje zasilania awaryjnego. Do dotacji instalacja musi móc pracować przy braku prądu.' }
  }
  return { status: 'sprawdz', krotko: 'nie wynika z oferty', opis: 'Z oferty nie wynika, czy instalacja będzie pracować przy braku prądu w sieci.' }
}

/**
 * Ocena warunków PME2. Zwraca null, gdy nie znamy pojemności magazynu.
 * `cenaOd`/`cenaDo`: ceny samego magazynu w sklepach, brutto (jak w katalogu).
 */
export const ocenaPme2 = ({ pojemnoscKwh, kwp, zasilanieAwaryjne, backupFalownika, cenaOd, cenaDo }) => {
  const kwh = Number(pojemnoscKwh)
  if (!Number.isFinite(kwh) || kwh <= 0) return null

  // Dokładnie 2 × kWp, bez zaokrąglania w górę: 9,025 kWp wymaga 18,05 kWh, a 18,08 kWh już spełnia.
  // Zaokrąglenie do 0,1 kWh (18,1) oblewało takie oferty (błąd z 2026-09-29).
  const minDlaPv = kwp ? Math.round(kwp * KWH_NA_KWP * 100) / 100 : null
  const minPojemnosc = Math.max(MIN_POJEMNOSC_KWH, minDlaPv || 0)
  const limitCeny = MAKS_CENA_ZA_KWH * kwh

  const warunki = []
  warunki.push({
    klucz: 'pojemnosc', tytul: `Pojemność od ${MIN_POJEMNOSC_KWH} kWh`,
    status: kwh >= MIN_POJEMNOSC_KWH ? 'tak' : 'nie',
    krotko: `w ofercie ${liczba(kwh)} kWh`,
    opis: `Magazyn w ofercie: ${liczba(kwh)} kWh. Liczy się pojemność nominalna z karty katalogowej.`,
  })
  warunki.push(kwp ? {
    klucz: 'dwa_do_jednego', tytul: '2 kWh magazynu na każdy kWp',
    status: kwh >= minDlaPv - 1e-9 ? 'tak' : 'nie',
    krotko: `potrzeba ${liczba(minDlaPv)} kWh`,
    opis: `Zasada 2:1. Instalacja ${liczba(kwp)} kWp wymaga magazynu co najmniej ${liczba(minDlaPv)} kWh, w ofercie jest ${liczba(kwh)} kWh.`,
  } : {
    klucz: 'dwa_do_jednego', tytul: '2 kWh magazynu na każdy kWp',
    status: 'sprawdz', krotko: 'nie znamy mocy', opis: 'Zasada 2:1. Nie znamy mocy instalacji, więc nie sprawdzimy tego warunku.',
  })

  const znanaCena = cenaOd != null && cenaDo != null
  const jednaCena = znanaCena && Math.abs(cenaDo - cenaOd) < 10
  const sklep = !znanaCena ? ''
    : jednaCena ? ` Sam magazyn kosztuje w sklepach ok. ${zl(cenaOd)} brutto, bez montażu.`
      : ` Sam magazyn kosztuje w sklepach ${zl(cenaOd)} do ${zl(cenaDo)} brutto, bez montażu.`
  // „nie” tylko wtedy, gdy już najtańszy magazyn w sklepie, bez montażu, przekracza limit.
  // Limit dotyczy kwot brutto z faktur (wzór protokołu odbioru 22.10.2026), więc porównujemy brutto.
  const zaDrogi = cenaOd != null && cenaOd > limitCeny
  warunki.push({
    klucz: 'cena', tytul: `Do ${MAKS_CENA_ZA_KWH.toLocaleString('pl-PL')} zł za kWh z montażem`,
    status: zaDrogi ? 'nie' : 'sprawdz',
    krotko: `limit ${zl(limitCeny)}`,
    opis: `Dla ${liczba(kwh)} kWh to najwyżej ${zl(limitCeny)} brutto za magazyn z montażem, bez falownika i EMS.${sklep}`
      + (zaDrogi ? ' Już sam magazyn przekracza limit.' : ' Poproś o osobną cenę magazynu na fakturze.'),
  })

  warunki.push({ klucz: 'wyspa', tytul: 'Praca przy braku prądu', ...wyspa(zasilanieAwaryjne, backupFalownika) })
  warunki.push({
    klucz: 'ems', tytul: 'System EMS zgodny z programem', status: 'sprawdz', krotko: 'zapytaj instalatora',
    opis: 'Nie wystarczy aplikacja z wykresami. EMS ma podejmować decyzje na podstawie danych pogodowych i ekonomicznych, sterować ładowaniem i rozładowaniem magazynu, pracować według profili czasowych i spełniać wymagania cyberbezpieczeństwa (m.in. ETSI EN 303 645). Nazwę, rodzaj i funkcje EMS wpisuje się do protokołu odbioru.',
  })
  warunki.push({
    klucz: 'ty', tytul: 'Warunki po Twojej stronie', status: 'ty', krotko: 'sprawdź sam',
    opis: 'Jesteś osobą fizyczną i stroną umowy z dostawcą energii, umowa nie dotyczy działalności gospodarczej pod tym adresem, a fotowoltaika jest przyłączona do sieci. Wniosek składasz dopiero po zakupie, montażu i zapłacie (od 1 listopada 2025 r.) oraz po zgłoszeniu magazynu do operatora sieci, w generatorze wniosków NFOŚiGW, z profilem zaufanym.',
  })

  const kwotaMaks = Math.min(ZL_ZA_KWH * kwh, LIMIT_NET_BILLING)
  const zCeny = (c) => Math.min(PROCENT_KOSZTOW * c, kwotaMaks)
  return {
    pojemnosc: kwh,
    minPojemnosc,
    limitCeny,
    warunki,
    spelnia: !warunki.some((w) => w.status === 'nie'),
    // Pojemność albo 2:1 do poprawy: da się to naprawić większym magazynem.
    zaMaly: warunki.some((w) => ['pojemnosc', 'dwa_do_jednego'].includes(w.klucz) && w.status === 'nie'),
    kwotaMaks,
    kwotaNetMetering: Math.min(ZL_ZA_KWH * kwh, LIMIT_NET_METERING),
    // Premia za falownik albo magazyn z UE i limit na punkt poboru (do opisu w raporcie).
    premiaUe: PREMIA_UE,
    limitNaPpe: LIMIT_NA_PPE,
    // Od tej ceny magazynu z montażem 30% kosztów przestaje być najniższą z trzech kwot.
    cenaPelnejKwoty: kwotaMaks / PROCENT_KOSZTOW,
    // 30% ceny samego magazynu w sklepach, nie więcej niż kwotaMaks. null bez ceny.
    kwotaWgSklepu: znanaCena ? { od: zCeny(cenaOd), do: zCeny(cenaDo), cenaOd, cenaDo } : null,
  }
}

/** Pytania do instalatora wynikające z PME2. Tylko przy magazynie z ocenioną pojemnością. */
export const pytaniaPme2 = (ocena, zasilanieAwaryjne) => {
  if (!ocena) return []
  const q = []
  if (ocena.zaMaly) {
    q.push(`Ile kosztowałby magazyn o pojemności co najmniej ${liczba(ocena.minPojemnosc)} kWh, który spełnia warunki dotacji PME2?`)
  }
  q.push(`Czy na fakturze będzie osobno cena magazynu z montażem, bez falownika i EMS? Do dotacji PME2 nie może przekroczyć ${zl(ocena.limitCeny)}.`)
  q.push('Czy system EMS w ofercie spełnia wymagania programu PME2 (decyzje na podstawie danych pogodowych i cen, profile czasowe, cyberbezpieczeństwo) i czy wpiszecie jego nazwę, rodzaj i funkcje do protokołu odbioru?')
  q.push(`Gdzie wyprodukowano magazyn i falownik? Sprzęt z UE daje premię do ${zl(PREMIA_UE)} w PME2 (kraj sprawdzają na tabliczce znamionowej).`)
  if (zasilanieAwaryjne === 'nie') {
    q.push('Czy możecie dodać zasilanie awaryjne (pracę wyspową)? Bez niego magazyn nie dostanie dotacji PME2.')
  }
  return q
}
